import React, { useEffect, useMemo, useState } from "react";

import { Button, Checkbox, Icon, List, Modal } from "semantic-ui-react";

// common
import SearchBox from "react-lib/appcon/common/SearchBox";
import SearchBoxWithKey from "react-lib/appcon/common/SearchBoxWithKey";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import ModError from "react-lib/appcon/common/ModInfo";
import ModConfirm from "react-lib/apps/common/cnmi/ModConfirm";

// STYLE
import "react-lib/apps/HISV3/common/CardOrderSetTemplate.scss";
import { ADMIN_NAME_LIST } from "react-lib/apps/USER/CardSettingDivision";

import FormOrderDetailList from "react-lib/apps/HISV3/common/FormOrderDetailList";
import CardSupplyOrderQA from "react-lib/apps/HISV3/MSD/CardSupplyOrderQA";
import CardOrderSetTemplateUX from "react-lib/apps/HISV3/common/CardOrderSetTemplateUX";
import CardAddOrderSetTemplateUX from "react-lib/apps/HISV3/common/CardAddOrderSetTemplateUX";
import ModTreatmentItemsContainer from "react-lib/apps/HISV3/common/ModTreatmentItemsContainer";

import {
  adminMenuDivisionType,
  ImagingTemplateList,
  LabTemplateList,
  MasterOptionsType,
  MiscellaneousTemplateList,
  OptionType,
  OrderSetTemplateType,
  SearchedItemList,
  SearchTemplateType,
  SelectedDivision,
  SelectTemplateType,
  SetProp,
  SupplyTemplateList,
  TreatmentTemplateList,
} from "./sequence/OrderSetTemplate";

const CARD_KEY: string = "CardOrderSetTemplate";

const typeOrderOption = [
  { key: "ALL", value: "ALL", text: "ทั้งหมด" },
  { key: "G", value: "G", text: "ชุดคำสั่งกลาง" },
  { key: "D", value: "D", text: "ชุดคำสั่งประจำแผนก" },
  { key: "P", value: "P", text: "ชุดคำสั่งแพทย์" },
];

const typeOrderDetailOption = [
  { key: "ALL", value: "ALL", text: "ALL" },
  { key: "PROC", value: "PROC", text: "Treatment" },
  { key: "SUPPLY", value: "SUPPLY", text: "Supply" },
  { key: "LAB", value: "LAB", text: "Lab" },
  { key: "XRAY", value: "XRAY", text: "X-ray" },
  { key: "OTHER", value: "OTHER", text: "Miscellaneous" },
];

type CardOrderSetTemplateProps = {
  runSequence: (e: any) => any;
  onEvent: (e: any) => Promise<any>;
  setProp: SetProp;
  django?: Record<any, string>;
  currentDoctor?: Record<any, string>;

  // sequence
  OrderSetTemplateSequence?: OrderSetTemplateType;
  isAddTemplate?: boolean;
  // common
  errorMessage?: Record<string, any>;
  buttonLoadCheck?: Record<string, any>;
  selectedDivision?: SelectedDivision;
  adminMenuDivisions?: adminMenuDivisionType;
  searchedItemListWithKey?: Record<string, any>;
  searchedItemList?: Record<string, any>;
  masterOptions?: MasterOptionsType;
  masterData?: MasterOptionsType;
  supplyOrderEligibilityOptions?: OptionType[];
  languageUX?: string;
};

const CardOrderSetTemplate = (props: CardOrderSetTemplateProps) => {
  const [searchProduct, setSearchProduct] = useState(null);
  const [typeOrderDetail, setTypeOrderDetail] = useState<
    "ALL" | "LAB" | "XRAY" | "PROC" | "SUPPLY" | "OTHER"
  >("ALL");
  const [showTemplateDetail, setShowTemplateDetail] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<"ALL" | "LAB" | "XRAY" | "PROC" | "SUPPLY" | "OTHER">(
    "ALL"
  );

  useEffect(() => {
    props.runSequence({
      sequence: "OrderSetTemplate",
      cardKey: CARD_KEY,
      restart: true,
    });
  }, []);

  const doctorName = useMemo(() => {
    const isDoctor = props.django?.user?.role_types?.includes("DOCTOR");
    const doctorNameCode = props.currentDoctor?.name_code || "";
    if (isDoctor) {
      return doctorNameCode.replace(/\s*\(.*?\)\s*/g, "").trim() || "";
    } else {
      return "";
    }
  }, [props.currentDoctor, props.selectedDivision, props.django]);

  const divisionOptions = useMemo(
    () => props.adminMenuDivisions?.all[ADMIN_NAME_LIST["Order Set Template"]]?.divisions || [],
    [props.adminMenuDivisions, props.selectedDivision]
  );

  const templateDivisionOption = useMemo(() => {
    const divisions = props.adminMenuDivisions?.all["Order Set Template"]?.divisions || [];

    return [{ key: "ALL", text: "ทั้งหมด", value: "ALL" }, ...divisions];
  }, [props.adminMenuDivisions, props.selectedDivision]);

  const productList = useMemo(() => {
    return (props.searchedItemList || [])
      .filter((item: any) => {
        const group = ["LAB", "XRAY", "PROC", "SUPPLY"];

        if (group.includes(typeOrderDetail)) {
          return item?.p_type_code === typeOrderDetail;
        }

        if (typeOrderDetail === "OTHER") {
          return !group.includes(item?.p_type_code);
        }

        return true;
      })
      ?.map((item: any) => {
        const displayType =
          typeOrderDetail === "XRAY"
            ? "X-ray"
            : typeOrderDetailOption.find((option: any) => option.value === item?.p_type_code)
                ?.text || "Miscellaneous";

        return {
          ...item,
          ...(typeOrderDetail === "XRAY" && { p_type_code: "XRAY" }),
          codeProduct: <div style={{ marginLeft: "20px" }}>{item?.code}</div>,
          nameProduct: <div style={{ marginLeft: "20px" }}>{item?.name}</div>,
          typeProduct: <div style={{ marginLeft: "20px" }}>{displayType}</div>,
        };
      });
  }, [props.searchedItemList, props.OrderSetTemplateSequence?.searchTemplate?.type]);

  const handleChangeSearchData = (e: any, v: any) => {
    type searchTemplateProp = `OrderSetTemplateSequence.searchTemplate.${keyof SearchTemplateType}`;

    const value = v.type === "checkbox" ? v.checked : v.value;
    const key = `OrderSetTemplateSequence.searchTemplate.${v.name}` as searchTemplateProp;

    if (v.name === "type") {
      const extra = v.value === "D" ? { doctor: "" } : v.value === "P" && { division: "" };

      props.setProp("OrderSetTemplateSequence.searchTemplate", {
        ...props.OrderSetTemplateSequence?.searchTemplate,
        type: value,
        ...extra,
      });
    } else {
      props.setProp(key, value);
    }
  };

  const handleChangeData = (e: any, v: any) => {
    type selectTemplateProp = `OrderSetTemplateSequence.selectTemplate.${keyof SelectTemplateType}`;

    const value = v.type === "checkbox" ? v.checked : v.value;
    const key = `OrderSetTemplateSequence.selectTemplate.${v.name}` as selectTemplateProp;

    if (v.name === "type") {
      const extra = v.value === "D" ? { doctor: null } : v.value === "P" ? { division: null } : {};

      props.setProp("OrderSetTemplateSequence.selectTemplate", {
        ...props.OrderSetTemplateSequence?.selectTemplate,
        type: value,
        ...extra,
      });
    } else if (v.name === "typeOrderDetail") {
      setTypeOrderDetail(value);
    } else {
      props.setProp(key, value);
    }
  };

  const handleSearchDoctorTemplate = (value: any) => {
    handleChangeSearchData(null, { name: "doctor", value });
  };

  const handleAnesthesiologist = (value: any) => {
    handleChangeData(null, { name: "doctor", value });
  };

  const handleNewOrderTemplate = () => {
    setSearchProduct(null);
    setTypeOrderDetail("ALL");
    setShowTemplateDetail(true);

    props.setProp(`OrderSetTemplateSequence`, {
      ...props.OrderSetTemplateSequence,
      selectTemplate: {
        type: "G",
        lab_template_list: [],
        imaging_template_list: [],
        treatment_template_list: [],
        supply_template_list: [],
      },
    });
  };

  const handleSelectedProductList = (item: Record<string, any>) => {
    if (item?.p_type_code === "LAB") {
      props.setProp(`OrderSetTemplateSequence.selectTemplate.lab_template_list`, [
        ...((props.OrderSetTemplateSequence?.selectTemplate?.lab_template_list ||
          []) as LabTemplateList[]),
        {
          ...item,
          test: item?.id,
          quantity: 1,
          note: "",
          urgency: 1, // default
          specimen_time: "00:00", // default
        } as LabTemplateList,
      ]);
    } else if (item?.p_type_code === "XRAY") {
      props.setProp(`OrderSetTemplateSequence.selectTemplate.imaging_template_list`, [
        ...((props.OrderSetTemplateSequence?.selectTemplate?.imaging_template_list ||
          []) as ImagingTemplateList[]),
        {
          ...item,
          product: item?.id,
          quantity: 1,
          note: "",
          urgency: 1, // default
          eligibility_type: 1, // default
        } as ImagingTemplateList,
      ]);
    } else if (item?.p_type_code === "PROC") {
      props.setProp(`OrderSetTemplateSequence.selectTemplate.treatment_template_list`, [
        ...((props.OrderSetTemplateSequence?.selectTemplate?.treatment_template_list ||
          []) as TreatmentTemplateList[]),
        {
          ...item,
          product: item?.id,
          quantity: 1,
          remark: "",
          eligibility_type: 1, // default
          to_be_performer: 1, // default
        } as TreatmentTemplateList,
      ]);
    } else if (item?.p_type_code === "SUPPLY") {
      props.setProp(`OrderSetTemplateSequence.selectTemplate.supply_template_list`, [
        ...((props.OrderSetTemplateSequence?.selectTemplate?.supply_template_list ||
          []) as SupplyTemplateList[]),
        {
          ...item,
          supply: item?.id,
          quantity: 1,
          remark: "",
          eligibility_type: 1, // default
        } as SupplyTemplateList,
      ]);
    } else if (item?.p_type_code) {
      props.setProp(`OrderSetTemplateSequence.selectTemplate.miscellaneous_template_list`, [
        ...((props.OrderSetTemplateSequence?.selectTemplate?.miscellaneous_template_list ||
          []) as MiscellaneousTemplateList[]),
        {
          ...item,
          product: item?.id,
          quantity: 1,
          eligibility_type: 1, // default
        } as MiscellaneousTemplateList,
      ]);
    }
  };

  const handleSelectedTemplate = (id: number) => {
    setSearchProduct(null);
    setShowTemplateDetail(true);
    setTypeOrderDetail("ALL");

    props.runSequence({
      sequence: "OrderSetTemplate",
      action: "getTemplateDetail",
      cardKey: CARD_KEY,
      pk: id,
    });
  };

  const handleCloseTemplate = () => {
    setShowTemplateDetail(false);
    props.setProp("OrderSetTemplateSequence.selectTemplate", {});
  };

  const handleDeleteTemplate = () => {
    props.runSequence({
      sequence: "OrderSetTemplate",
      action: "deleteTemplate",
      cardKey: CARD_KEY,
      callback: (open: boolean) => {
        setShowTemplateDetail(open);
      },
    });
  };

  const handleSearchTemplate = () => {
    setShowTemplateDetail(false);

    props.runSequence({
      sequence: "OrderSetTemplate",
      action: "searchTemplate",
      cardKey: CARD_KEY,
    });
  };

  const handleComfrimOrder = () => {
    if (props.OrderSetTemplateSequence?.checkBoxTemplate?.SUPPLY?.length !== 0) {
      props.runSequence({
        sequence: "OrderSetTemplate",
        action: "previewSupply",
        cardKey: CARD_KEY,
        callback: (open: boolean) => {
          setShowTemplateDetail(open);
        },
      });
    } else {
      props.runSequence({
        sequence: "OrderSetTemplate",
        action: "orderData",
        cardKey: CARD_KEY,
        callback: (open: boolean) => {
          setShowTemplateDetail(open);
        },
      });
    }
  };

  const handleSaveAnswer = (supplyQA: any) => {
    props.runSequence({
      sequence: "OrderSetTemplate",
      action: "saveAnswer",
      cardKey: CARD_KEY,
      supplyData: supplyQA,
      callback: (open: boolean) => {
        setShowTemplateDetail(open);
      },
    });
  };

  const handleSaveDupLab = () => {
    props.setProp(`OrderSetTemplateSequence.duplicateLabDetected`, []);

    props.runSequence({
      sequence: "OrderSetTemplate",
      action: "orderData",
      cardKey: CARD_KEY,
      isOrderLab: true,
      allow_duplicate_flag: true,
      callback: (open: boolean) => {
        setShowTemplateDetail(open);
      },
    });
  };

  const handleCloseModConfirm = () => {
    props.setProp(`OrderSetTemplateSequence.duplicateLabDetected`, []);
  };

  const handleOpenModalSearch = () => {
    if (typeOrderDetail === "PROC") {
      props.runSequence({
        sequence: "OrderSetTemplate",
        action: "treatmentSearch",
        cardKey: CARD_KEY,
        page: 0,
        restart: true
      });
    } else if (typeOrderDetail === "SUPPLY") {
      props.runSequence({
        sequence: "OrderSetTemplate",
        action: "supplySearch",
        cardKey: CARD_KEY,
        page: 0,
        restart: true
      });
    } else if (typeOrderDetail === "LAB") {
      props.runSequence({
        sequence: "OrderSetTemplate",
        action: "labSearch",
        cardKey: CARD_KEY,
        page: 0,
        restart: true
      });
    }

    setOpenModal(typeOrderDetail);
  };

  return (
    <>
      {props.isAddTemplate ? (
        <CardAddOrderSetTemplateUX
          doctorName={doctorName}
          divisionName={props.selectedDivision?.name}
          typeOrderOption={typeOrderOption}
          showTemplateDetail={showTemplateDetail}
          searchTemplate={props.OrderSetTemplateSequence?.searchTemplate}
          onChangeSearchData={handleChangeSearchData}
          templateList={
            <div style={{ overflow: "auto", maxHeight: "50vh" }}>
              <List selection celled verticalAlign="middle">
                {(props.OrderSetTemplateSequence?.templateList || [])
                  .filter(
                    (item: any) =>
                      item?.type === "G" ||
                      item?.division === props.selectedDivision?.id ||
                      item?.doctor === props.currentDoctor?.id
                  )
                  .map((item: any, index: number) => {
                    const { id, name, type } = item;
                    const selectedId = props.OrderSetTemplateSequence?.selectTemplate?.id;
                    const typeText = typeOrderOption?.find(
                      (opt: OptionType) => opt.value === type
                    )?.text;

                    return (
                      <List.Item
                        key={id ?? index}
                        style={{
                          background: selectedId === id ? "#D6ECF3" : undefined,
                          borderTop: index === 0 ? "none" : undefined,
                        }}
                        onClick={() => handleSelectedTemplate(id)}
                      >
                        <List.Content floated="left">
                          <List.Header>{name}</List.Header>
                        </List.Content>
                        <List.Content floated="right">
                          <div style={{ color: "gray" }}>{`[${typeText ?? type}]`}</div>
                        </List.Content>
                      </List.Item>
                    );
                  })}
              </List>
            </div>
          }
          orderDetailList={
            <FormOrderDetailList
              runSequence={props.runSequence}
              setProp={props.setProp}
              masterOptions={props.masterOptions}
              masterData={props.masterData}
              supplyOrderEligibilityOptions={props.supplyOrderEligibilityOptions}
              buttonLoadCheck={props.buttonLoadCheck}
              OrderSetTemplateSequence={props.OrderSetTemplateSequence}
              isAddTemplate={props.isAddTemplate}
            />
          }
          buttonSearchTemplate={
            <ButtonLoadCheck
              // function
              setProp={props.setProp}
              onClick={handleSearchTemplate}
              // data
              paramKey={`${CARD_KEY}_searchTemplate`}
              buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_KEY}_searchTemplate`]}
              // config
              color="blue"
              size="small"
              title={props.languageUX === "en" ? "Search" : "ค้นหา"}
            />
          }
          buttonComfrimOrder={
            <ButtonLoadCheck
              // function
              setProp={props.setProp}
              onClick={handleComfrimOrder}
              // data
              paramKey={`${CARD_KEY}${
                props.OrderSetTemplateSequence?.checkBoxTemplate?.SUPPLY?.length !== 0
                  ? "_previewSupply"
                  : "_orderData"
              }`}
              buttonLoadCheck={
                props.buttonLoadCheck?.[
                  `${CARD_KEY}${
                    props.OrderSetTemplateSequence?.checkBoxTemplate?.SUPPLY?.length !== 0
                      ? "_previewSupply"
                      : "_orderData"
                  }`
                ]
              }
              // config
              color="green"
              size="small"
              title="Comfrim order"
            />
          }
        />
      ) : (
        <CardOrderSetTemplateUX
          typeOrderOption={typeOrderOption}
          typeOrderDetailOption={typeOrderDetailOption}
          showTemplateDetail={showTemplateDetail}
          orderDivisionOption={divisionOptions}
          templateDivisionOption={templateDivisionOption}
          typeOrderDetail={typeOrderDetail}
          orderDetail={props.OrderSetTemplateSequence?.selectTemplate}
          searchTemplate={props.OrderSetTemplateSequence?.searchTemplate}
          onChangeData={handleChangeData}
          onChangeSearchData={handleChangeSearchData}
          searchDoctorTemplate={
            <SearchBoxWithKey
              // callback
              onEvent={props.onEvent}
              type="Doctor"
              id={"doctor"}
              inline
              searchedItemListWithKey={props.searchedItemListWithKey}
              selectedItem={props.OrderSetTemplateSequence?.searchTemplate?.doctor || null}
              setSelected={handleSearchDoctorTemplate}
              defaultText={
                props.OrderSetTemplateSequence?.searchTemplate?.doctor === "ALL" ? "ทั้งหมด" : ""
              }
              disabled={props.OrderSetTemplateSequence?.searchTemplate?.type === "D"}
            />
          }
          templateList={
            <div style={{ overflow: "auto", maxHeight: "65vh" }}>
              <List selection celled verticalAlign="middle">
                {(props.OrderSetTemplateSequence?.templateList || []).map(
                  (item: any, index: number) => {
                    const { id, name, type } = item;
                    const selectedId = props.OrderSetTemplateSequence?.selectTemplate?.id;
                    const typeText = typeOrderOption?.find(
                      (opt: OptionType) => opt.value === type
                    )?.text;

                    return (
                      <List.Item
                        key={id ?? index}
                        style={{
                          background: selectedId === id ? "#D6ECF3" : undefined,
                          borderTop: index === 0 ? "none" : undefined,
                        }}
                        onClick={() => handleSelectedTemplate(id)}
                      >
                        <List.Content floated="left">
                          <List.Header>{name}</List.Header>
                        </List.Content>
                        <List.Content floated="right">
                          <div style={{ color: "gray" }}>{`[${typeText ?? type}]`}</div>
                        </List.Content>
                      </List.Item>
                    );
                  }
                )}
              </List>
            </div>
          }
          orderDetailList={
            <FormOrderDetailList
              runSequence={props.runSequence}
              setProp={props.setProp}
              masterOptions={props.masterOptions}
              masterData={props.masterData}
              supplyOrderEligibilityOptions={props.supplyOrderEligibilityOptions}
              buttonLoadCheck={props.buttonLoadCheck}
              OrderSetTemplateSequence={props.OrderSetTemplateSequence}
            />
          }
          addNewOrder={
            <Button color="green" size="mini" icon onClick={handleNewOrderTemplate}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Icon name="plus square outline" size="large" />
                สร้างชุดคำสั่งใหม่
              </div>
            </Button>
          }
          searchDoctorDetail={
            <SearchBoxWithKey
              // callback
              onEvent={props.onEvent}
              placeholder="ชื่อแพทย์"
              type="Doctor"
              id={"doctor"}
              inline
              width="100%"
              searchedItemListWithKey={props.searchedItemListWithKey}
              selectedItem={props.OrderSetTemplateSequence?.selectTemplate?.doctor || null}
              setSelected={handleAnesthesiologist}
              disabled={props.OrderSetTemplateSequence?.selectTemplate?.type !== "P"}
              defaultText={props.OrderSetTemplateSequence?.selectTemplate?.doctorName || ""}
            />
          }
          searchOrderList={
            <SearchBox
              onEvent={props.onEvent}
              // config
              type={"ProductAll"}
              style={{ width: "100%" }}
              fluid={true}
              useWithKey={false}
              icon="search"
              // Select
              searchedItemList={productList}
              selectedItem={searchProduct || null}
              setSelectedItem={handleSelectedProductList}
              vertical={true}
              showTable={true}
              widths="120,auto,auto"
              headers="Code,ชื่อ,ประเภท"
              keys="codeProduct,nameProduct,typeProduct"
              toDisplay={(item: any) => item.name}
            />
          }
          closeTemplate={handleCloseTemplate}
          buttonSearchTemplate={
            <ButtonLoadCheck
              // function
              setProp={props.setProp}
              onClick={handleSearchTemplate}
              // data
              paramKey={`${CARD_KEY}_searchTemplate`}
              buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_KEY}_searchTemplate`]}
              // config
              color="blue"
              size="small"
              title={props.languageUX === "en" ? "Search" : "ค้นหา"}
            />
          }
          buttonSaveTemplate={
            <ButtonLoadCheck
              // function
              setProp={props.setProp}
              onClick={() => {
                props.runSequence({
                  sequence: "OrderSetTemplate",
                  action: "saveTemplate",
                  cardKey: CARD_KEY,
                  callback: (open: boolean) => {
                    setShowTemplateDetail(open);
                  },
                });
              }}
              // data
              paramKey={`${CARD_KEY}_saveTemplate`}
              buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_KEY}_saveTemplate`]}
              // config
              color="green"
              size="mini"
              title={
                props.languageUX === "en"
                  ? "Confirm creation of command set"
                  : "ยืนยันการสร้างชุดคำสั่ง"
              }
            />
          }
          buttonEditTemplate={
            <ButtonLoadCheck
              // function
              setProp={props.setProp}
              onClick={() => {
                props.runSequence({
                  sequence: "OrderSetTemplate",
                  action: "editTemplate",
                  cardKey: CARD_KEY,
                  callback: (open: boolean) => {
                    setShowTemplateDetail(open);
                  },
                });
              }}
              // data
              paramKey={`${CARD_KEY}_editTemplate`}
              buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_KEY}_editTemplate`]}
              // config
              color="yellow"
              size="mini"
              title={"บันทึกการแก้ไขชุดคำสั่ง"}
            />
          }
          buttonDeleteTemplate={
            <ButtonLoadCheck
              // function
              setProp={props.setProp}
              onClick={handleDeleteTemplate}
              // data
              paramKey={`${CARD_KEY}_deleteTemplate`}
              buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_KEY}_deleteTemplate`]}
              // config
              color="red"
              size="mini"
              title={"ลบชุดคำสั่ง"}
            />
          }
          buttonSearch={
            <ButtonLoadCheck
              // function
              setProp={props.setProp}
              onClick={handleOpenModalSearch}
              // data
              // paramKey={`${CARD_KEY}`}
              // buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_KEY}`]}
              // config
              style={{display: "none"}}
              color="blue"
              size="small"
              title={"ค้นหา"}
            />
          }
        />
      )}

      {["saveTemplate", "editTemplate", "orderData"].map((action) => (
        <ModError
          key={action}
          open={props.errorMessage?.[`${CARD_KEY}_${action}`]}
          titleColor="red"
          size="small"
          onApprove={() => {
            props.setProp(`errorMessage.${CARD_KEY}_${action}`, null);
          }}
          alertText={(props.errorMessage?.[`${CARD_KEY}_${action}`] || []).map(
            (text: string, i: number) => (
              <div key={i}>{text}</div>
            )
          )}
        />
      ))}

      <ModConfirm
        openModal={(props.OrderSetTemplateSequence?.duplicateLabDetected || [])?.length > 0}
        titleName={"Duplicate Lab Test Detected"}
        titleColor="red"
        size="mini"
        // @ts-ignore
        content={
          <div>
            <div>{props.OrderSetTemplateSequence?.duplicateLabDetected?.[0] || ""}</div>
            <div style={{ margin: "0px 20px" }}>
              <ul>
                {(props.OrderSetTemplateSequence?.duplicateLabDetected?.[1] || "")
                  .split("<br/>")
                  .map((line) => {
                    return <li>{line?.split(">")?.[1]}</li>;
                  })}
              </ul>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                fontWeight: "bold",
                marginTop: "20px",
              }}
            >
              {"Do you still want to proceed this order?"}
            </div>
          </div>
        }
        onApprove={handleSaveDupLab}
        onDeny={handleCloseModConfirm}
      />

      {props.OrderSetTemplateSequence?.supplyQA?.map((item: any, index: any) => {
        return (
          <CardSupplyOrderQA
            supplyQA={item}
            // index={index}
            runSequence={props.runSequence}
            setProp={props.setProp}
            // action
            onSave={() => handleSaveAnswer(item)}
            onClose={() => {
              props.setProp(`OrderSetTemplateSequence.supplyQA`, []);
            }}
            onChangeAnswer={(answer: any) => {
              props.setProp(`OrderSetTemplateSequence.supplyQA.${index}.answer_dict`, answer);
            }}
            languageUX={props.languageUX}
          />
        );
      })}

      {/* <ModTreatmentItemsContainer
        openModal={openModal}
        setOpenModal={setOpenModal}
        setShowTemplateDetail={setShowTemplateDetail}
        typeOrderDetail={typeOrderDetail}
        runSequence={props.runSequence}
        setProp={props.setProp}
        OrderSetTemplateSequence={props.OrderSetTemplateSequence}
        cardKey={CARD_KEY}
        masterOptions={props.masterOptions}
        masterData={props.masterData}
      /> */}
    </>
  );
};

CardOrderSetTemplate.displayName = "CardOrderSetTemplate";

export default React.memo(CardOrderSetTemplate);
